/**
 * NotesJournal Component
 *
 * A journal-style view for notes that shows entries grouped by date.
 * Features a quick-entry field at the top and chronological entries below.
 */

import { useMemo, useCallback } from 'react';
import { FileText } from 'lucide-react';
import { useData } from '@/context/DataContext';
import { QuickNoteEntry } from './QuickNoteEntry';
import { JournalEntry } from './JournalEntry';
import { formatFullDate } from '@/utils/dates';
import type { NoteItem } from '@/types';

// ============================================
// TYPES
// ============================================

interface DateGroup {
  date: string;
  displayDate: string;
  notes: NoteItem[];
}

// ============================================
// HELPERS
// ============================================

/**
 * Group notes by date and sort chronologically (newest first)
 */
function groupNotesByDate(notes: NoteItem[]): DateGroup[] {
  // Sort notes by createdAt descending (newest first)
  const sorted = [...notes].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  // Group by date
  const groups: Record<string, NoteItem[]> = {};

  for (const note of sorted) {
    const datePart = note.createdAt.split('T')[0] ?? note.createdAt.slice(0, 10);
    if (!groups[datePart]) {
      groups[datePart] = [];
    }
    groups[datePart].push(note);
  }

  // Convert to array with display dates
  return Object.entries(groups).map(([date, notes]) => ({
    date,
    displayDate: formatDateHeader(date),
    notes,
  }));
}

/**
 * Format date for section header
 */
function formatDateHeader(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00'); // Add time to avoid timezone issues
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Check if today
  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  }

  // Check if yesterday
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }

  // Format as full date
  return formatFullDate(dateStr);
}

/**
 * Generate auto title from current timestamp
 */
function generateAutoTitle(): string {
  const now = new Date();
  return now.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// ============================================
// DATE SECTION COMPONENT
// ============================================

interface DateSectionProps {
  displayDate: string;
  children: React.ReactNode;
}

function DateSection({ displayDate, children }: DateSectionProps) {
  return (
    <div className="mb-8">
      {/* Date header */}
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-sm font-semibold text-gray-900">{displayDate}</h3>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Entries */}
      <div className="space-y-6">{children}</div>
    </div>
  );
}

// ============================================
// EMPTY STATE
// ============================================

function EmptyState() {
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <FileText className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No journal entries yet</h3>
      <p className="text-sm text-gray-500 max-w-sm mx-auto">
        Start writing in the field above. Your entries will appear here, organized by date.
      </p>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function NotesJournal() {
  const { items, createItem, updateItem, deleteItem } = useData();
  const notes = items.notes;

  // Group notes by date
  const groupedNotes = useMemo(() => groupNotesByDate(notes), [notes]);

  // Handle creating new note
  const handleCreate = useCallback(
    (content: string, plainTextPreview: string) => {
      void createItem<NoteItem>({
        type: 'note',
        title: generateAutoTitle(),
        description: '',
        tags: [], // No inbox tag for journal entries
        isFavorite: false,
        content,
        plainTextPreview,
      });
    },
    [createItem]
  );

  // Handle updating note
  const handleUpdate = useCallback(
    (note: NoteItem) => {
      updateItem(note);
    },
    [updateItem]
  );

  // Handle deleting note
  const handleDelete = useCallback(
    (note: NoteItem) => {
      void deleteItem(note);
    },
    [deleteItem]
  );

  // Handle toggling favorite
  const handleToggleFavorite = useCallback(
    (note: NoteItem) => {
      updateItem({ ...note, isFavorite: !note.isFavorite });
    },
    [updateItem]
  );

  return (
    <div className="max-w-3xl mx-auto">
      {/* Quick entry at top */}
      <div className="mb-8">
        <QuickNoteEntry onSave={handleCreate} />
      </div>

      {/* Journal entries grouped by date */}
      {groupedNotes.length === 0 ? (
        <EmptyState />
      ) : (
        groupedNotes.map((group) => (
          <DateSection key={group.date} displayDate={group.displayDate}>
            {group.notes.map((note) => (
              <JournalEntry
                key={note.id}
                note={note}
                onSave={handleUpdate}
                onDelete={handleDelete}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </DateSection>
        ))
      )}
    </div>
  );
}
